import { useState } from 'react';

import { RotateCcwIcon, XIcon } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  SidebarWrapper,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { useResponsiveSidebarClose } from '@/components/organisms/Sidebar/ResponsiveSidebarPanel';
import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DimensionPicker } from '@/modules/analytics/components/CustomReports/DimensionPicker';
import { FilterChip } from '@/modules/analytics/components/CustomReports/FilterChip';
import { GroupBySelect } from '@/modules/analytics/components/CustomReports/GroupBySelect';
import { MetricCombobox } from '@/modules/analytics/components/CustomReports/MetricCombobox';
import { RangePicker } from '@/modules/analytics/components/CustomReports/RangePicker';
import {
  type AggregationFn,
  type FilterType,
  getAggregationOptionsForMetric,
  getFilterOptionsForMetric,
  getGroupByOptionsForMetric,
  getMetricById,
} from '@/modules/analytics/constants/metricsCatalog';
import { isEnumBackedColumn } from '@/modules/analytics/hooks/useFilterColumnOptions';
import { useReport } from '@/modules/analytics/hooks/useReport';
import { useReportDetailUi } from '@/modules/analytics/hooks/useReportDetailUi';
import {
  EMPTY_METRIC_DRAFT,
  type MetricDraft,
  buildReportMetric,
  getDraftForMetric,
  hasAggregationChoice,
} from '@/modules/analytics/utils/metricDraft';
import {
  type FilterChip as FilterChipModel,
  type FilterOperator,
  getDefaultOperatorForType,
  isRatingColumn,
  operatorKeepsValues,
} from '@/modules/analytics/utils/reportFilters';
import { resolveMetricIndex } from '@/modules/analytics/utils/resolveMetricIndex';
import { CLEAR_DIRTY_VALUES } from '@/util/forms/resetOptions';

type Props = { reportId: string };

export const MetricEditorPanel = ({ reportId }: Props) => {
  const { t } = useTranslation();
  const onClose = useResponsiveSidebarClose();
  const { editor } = useReportDetailUi(reportId);
  const { report, refetch, updateReportAsync } = useReport(reportId);
  const { control, setValue, handleSubmit, reset, formState } = useFormContext<MetricDraft>();
  const [metricId, filters] = useWatch({ control, name: ['metricId', 'filters'] });
  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);

  const isEditing = editor?.kind === 'edit';
  const hasMetric = !!metricId;
  const primaryDimension = getMetricById(metricId)?.primaryDimension;
  const groupByOptions = getGroupByOptionsForMetric(metricId);
  const filterOptions = getFilterOptionsForMetric(metricId);
  const filterOptionByColumn = new Map(filterOptions.map((opt) => [opt.column, opt]));
  const activeFilterColumns = Object.keys(filters);

  const setFilters = (next: MetricDraft['filters']) =>
    setValue('filters', next, { shouldDirty: true });

  const handleMetricChange = (next: string) => {
    const draft = getDraftForMetric(next);
    setValue('metricId', next, { shouldDirty: true, shouldValidate: true });
    setValue('aggregationFn', draft.aggregationFn, { shouldValidate: true });
    setValue('groupBy', draft.groupBy);
    setValue('filters', draft.filters);
    setOpenFilterColumn(null);
  };

  const handlePickFilter = (column: string, type: FilterType) => {
    setOpenFilterColumn(column);
    if (filters[column]) {
      return;
    }
    const operator: FilterOperator =
      isEnumBackedColumn(column) || isRatingColumn(column)
        ? 'is_any_of'
        : getDefaultOperatorForType(type);
    const chip: FilterChipModel = { type, operator, values: type === 'boolean' ? ['true'] : [] };
    setFilters({ ...filters, [column]: chip });
  };

  const updateChip = (column: string, patch: Partial<FilterChipModel>) => {
    const current = filters[column];
    if (current) {
      setFilters({ ...filters, [column]: { ...current, ...patch } });
    }
  };

  const handleRemoveFilter = (column: string) => {
    const rest = { ...filters };
    delete rest[column];
    setFilters(rest);
    setOpenFilterColumn(null);
  };

  const onSubmit = async (draft: MetricDraft) => {
    if (!report) {
      return;
    }
    const { data: fresh } = await refetch();
    const current = fresh ?? report;
    const original = isEditing ? editor.original : undefined;
    const index = isEditing ? resolveMetricIndex(current.metrics, editor) : -1;
    if (isEditing && index === -1) {
      toast.error(t('analytics.reports_detail_metric_gone'));
      onClose();
      return;
    }
    const keptName = original?.metricId === draft.metricId ? original.name : undefined;
    const metric = buildReportMetric(draft, keptName);
    if (!metric) {
      return;
    }
    const metrics = isEditing
      ? current.metrics.map((m, i) => (i === index ? metric : m))
      : [...current.metrics, metric];
    await updateReportAsync({ metrics });
    if (isEditing) {
      toast.success(t('analytics.reports_detail_metric_updated'));
      onClose();
      return;
    }
    toast.success(t('analytics.reports_detail_metric_added'));
    reset(EMPTY_METRIC_DRAFT, CLEAR_DIRTY_VALUES);
    setOpenFilterColumn(null);
  };

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-6 font-semibold text-foreground">
          {isEditing
            ? t('analytics.reports_detail_edit_metric')
            : t('analytics.reports_detail_add_metric')}
        </h2>
        <Button
          variant="ghost-neutral"
          size="icon-sm"
          type="button"
          aria-label={t('common.close')}
          onClick={onClose}
        >
          <XIcon />
        </Button>
      </SidebarWrapperTitle>
      <form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        className="flex min-h-0 flex-1 flex-col gap-12 overflow-y-auto p-6"
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <FormField
              control={control}
              name="metricId"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-2 space-y-0">
                  <FormLabel className="mb-0">
                    {t('analytics.reports_detail_field_metric')}
                  </FormLabel>
                  <FormControl>
                    <MetricCombobox
                      id={field.name}
                      value={field.value}
                      onChange={handleMetricChange}
                      placeholder={t('analytics.reports_detail_select_placeholder')}
                      className="h-9 w-full"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {hasAggregationChoice(metricId) && (
              <FormField
                control={control}
                name="aggregationFn"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <Select
                      value={field.value}
                      onValueChange={(v) => field.onChange(v as AggregationFn)}
                    >
                      <FormControl>
                        <SelectTrigger
                          className="w-full"
                          aria-label={t('analytics.reports_detail_field_metric_type_placeholder')}
                        >
                          <SelectValue
                            placeholder={t(
                              'analytics.reports_detail_field_metric_type_placeholder'
                            )}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {getAggregationOptionsForMetric(metricId).map((opt) => (
                          <SelectItem key={opt.fn} value={opt.fn}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          {hasMetric && (
            <>
              <FormField
                control={control}
                name="groupBy"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2 space-y-0">
                    <FormLabel className="mb-0">{t('analytics.reports_detail_group_by')}</FormLabel>
                    <div className="flex items-center gap-3">
                      <FormControl>
                        <GroupBySelect
                          id={field.name}
                          className="flex-1"
                          options={groupByOptions}
                          value={field.value.filter((c) => c !== primaryDimension)}
                          onChange={(columns) =>
                            field.onChange([
                              ...(primaryDimension ? [primaryDimension] : []),
                              ...columns,
                            ])
                          }
                          disabled={groupByOptions.length === 0}
                        />
                      </FormControl>
                      <DimensionPicker
                        options={filterOptions}
                        onPick={(opt) => handlePickFilter(opt.column, opt.type)}
                        onOpenChange={(open) => {
                          if (open) {
                            setOpenFilterColumn(null);
                          }
                        }}
                      />
                    </div>
                    <FormDescription>{t('analytics.reports_detail_group_by_help')}</FormDescription>
                  </FormItem>
                )}
              />

              {activeFilterColumns.length > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-border p-1.5">
                  <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                    {activeFilterColumns.map((column) => {
                      const chip = filters[column];
                      const opt = filterOptionByColumn.get(column);
                      if (!opt || !chip) {
                        return null;
                      }
                      return (
                        <FilterChip
                          key={column}
                          column={column}
                          dimensionLabel={opt.label}
                          chip={chip}
                          onChangeOperator={(operator) =>
                            updateChip(column, {
                              operator,
                              values: operatorKeepsValues(column, chip.operator, operator)
                                ? chip.values
                                : [],
                            })
                          }
                          onChangeValues={(values) => updateChip(column, { values })}
                          onRemove={() => handleRemoveFilter(column)}
                          isValueOpen={openFilterColumn === column}
                          onValueOpenChange={(open) => setOpenFilterColumn(open ? column : null)}
                        />
                      );
                    })}
                  </div>
                  <InfoTooltip content={t('analytics.reports_detail_clear_all')}>
                    <Button
                      variant="ghost-neutral"
                      size="icon-sm"
                      type="button"
                      aria-label={t('analytics.reports_detail_clear_all')}
                      onClick={() => {
                        setFilters({});
                        setOpenFilterColumn(null);
                      }}
                    >
                      <RotateCcwIcon />
                    </Button>
                  </InfoTooltip>
                </div>
              )}

              <FormField
                control={control}
                name="range"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2 space-y-0">
                    <FormLabel className="mb-0">
                      {t('analytics.reports_detail_date_range')}
                    </FormLabel>
                    <FormControl>
                      <RangePicker
                        id={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="ghost-neutral"
            type="button"
            onClick={onClose}
            disabled={formState.isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" isLoading={formState.isSubmitting}>
            {isEditing ? t('common.save') : t('common.done')}
          </Button>
        </div>
      </form>
    </SidebarWrapper>
  );
};
